/**
 * Raw panel model (W2-05 easing-templates-raw; DESIGN.md §4.18 "Raw", U§5 C7, rpe-format.md F§6, F§11, F§12).
 *
 * The panel shows EXACTLY the JSON the exporter writes for the selection, from the engine's IR (`engine.irLine`,
 * falling back to `Lower.lowerLine` with the document's export settings) through `encodeRpeFragment`:
 *  - 'events':   one track's event list (a key, a segment or the focused column selects it);
 *  - 'notes':    the selected notes of one line, in export order;
 *  - 'line':     one line's own fields (name, texture, parent, cover, z order, group, controls, RPE-only fields);
 *  - 'controls': one note control curve of a line (pos, alpha, size, y).
 *
 * Editing: `checkRawText` parses the text with the lenient importer in fragment mode (`decodeRpeFragmentDetailed`):
 * every decoder diagnostic and every change normalisation would make is shown with its JSON path, and NOTHING is
 * repaired silently (a corrected version is offered as text the user can review). It then validates what the model
 * needs (events that can be lifted, the same number of notes, files and lines that exist). `applyRawEdit` is the
 * command body run in ONE transaction: it compares the new fragment with the exported one and changes only what
 * differs (an untouched fragment is a no-op, so applying never alters the export by itself):
 *  - events: lifted (`liftEventsChecked`, provenance kept) and written with `Core.keys.replaceTrackKeys`;
 *  - notes: per note, the fields that changed (raw triples, raw above / isFake integers, unknown keys kept);
 *  - line: `Core.lines` commands (patchLine, setTexture, setParent, setAttachUI, setControl) and the RPE-only fields;
 *  - controls: `Core.lines.setControl`.
 * Numbers are compared at f32 precision where the writer stores f32 (what the file can hold).
 */

import { Core } from '../../core/commands/index';
import type { Cmd } from '../../core/commands/types';
import { liftEventsChecked } from '../../core/ir/lift';
import { Lower } from '../../core/ir/lower';
import { controlIn, listKeyOf } from '../../core/ir/irShape';
import { groupLayout } from '../../core/ir/lowerCtx';
import { tripleEquals, tripleIn } from '../../core/ir/triples';
import type { IrControlPoint, IrEvent, IrLine, IrNote, LowerOptions } from '../../core/ir/types';
import { RPE_VISIBLE_ALWAYS } from '../../core/model/defaults';
import { PROP_SPECS, propRank } from '../../core/model/props';
import type {
  AssetId,
  ChartDoc,
  DocIndexes,
  GroupId,
  Hitsound,
  KeyId,
  Line,
  LineId,
  LineProp,
  Note,
  NoteId,
  NoteKind,
  NoteSrc,
  TrackId,
} from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import type { Json, MessageRef, RGB } from '../../core/util/types';
import {
  decodeRpeFragmentDetailed,
  diagnosticRef,
  RpeJsonSyntaxError,
  type FragmentControlKey,
  type RpeFragmentOptions,
} from '../../formats/rpe/decode';
import { encodeRpeFragment } from '../../formats/rpe/encode';
import type { EncodeStyle, RpeFragmentIr, RpeFragmentKind } from '../../formats/rpe/json';
import type { EventListKey } from '../../formats/rpe/schema';
import type { ColumnRef } from '../../state/types';
import { offsetOfPath } from './jsonPath';
import { m } from './raw.messages';

// ---------------------------------------------------------------------------------------------------------------
// Targets
// ---------------------------------------------------------------------------------------------------------------

export type ControlWhich = 'pos' | 'alpha' | 'size' | 'y';
export const CONTROL_WHICH: readonly ControlWhich[] = ['pos', 'alpha', 'size', 'y'];

export type RawTarget =
  | {
      readonly kind: 'events';
      readonly line: LineId;
      readonly track: TrackId;
      readonly prop: LineProp;
      readonly layer: number;
    }
  | { readonly kind: 'notes'; readonly line: LineId; readonly notes: readonly NoteId[] }
  | { readonly kind: 'line'; readonly line: LineId }
  | { readonly kind: 'controls'; readonly line: LineId; readonly which: ControlWhich };

/** Stable identity of a target (selection changes that keep it keep the panel's text). */
export function targetKey(t: RawTarget): string {
  switch (t.kind) {
    case 'events':
      return `events:${String(t.track)}`;
    case 'notes':
      return `notes:${String(t.line)}:${t.notes.join(',')}`;
    case 'line':
      return `line:${String(t.line)}`;
    case 'controls':
      return `controls:${String(t.line)}:${t.which}`;
  }
}

/** What the selection offers to the Raw panel. */
export interface RawSelection {
  readonly notes: ReadonlySet<NoteId>;
  readonly keys: ReadonlySet<KeyId>;
  readonly segments: ReadonlySet<KeyId>;
  readonly lines: ReadonlySet<LineId>;
}

function eventsTarget(doc: ChartDoc, track: TrackId): RawTarget | null {
  const t = doc.tracks.get(track);
  if (t?.owner.kind !== 'line') return null;
  return { kind: 'events', line: t.owner.line, track, prop: t.owner.prop, layer: t.owner.layer };
}

/**
 * Targets for the selection, best first: the tracks of the selected segments and keys (else the focused column's
 * track), the selected notes per line, then the selected lines (else the active line) with their control curves.
 */
export function rawTargets(
  doc: ChartDoc,
  idx: DocIndexes,
  sel: RawSelection,
  activeLine: LineId | null,
  focused: ColumnRef | null,
): RawTarget[] {
  const out: RawTarget[] = [];
  const lineRank = (l: LineId): number => idx.lineIndex(l);
  const tracks = new Set<TrackId>();
  for (const id of [...sel.segments, ...sel.keys]) {
    const k = doc.keys.get(id);
    if (k !== undefined) tracks.add(k.track);
  }
  if (tracks.size === 0 && focused !== null) {
    const id = idx.track(focused.line, focused.prop, PROP_SPECS[focused.prop].layered ? focused.layer : 0);
    if (id !== undefined) tracks.add(id);
  }
  const events = [...tracks]
    .map((id) => eventsTarget(doc, id))
    .filter((t): t is Extract<RawTarget, { kind: 'events' }> => t !== null)
    .sort((a, b) => lineRank(a.line) - lineRank(b.line) || propRank(a.prop) - propRank(b.prop) || a.layer - b.layer);
  out.push(...events);
  const notesByLine = new Map<LineId, Set<NoteId>>();
  for (const id of sel.notes) {
    const n = doc.notes.get(id);
    if (n === undefined) continue;
    const set = notesByLine.get(n.line) ?? new Set<NoteId>();
    set.add(id);
    notesByLine.set(n.line, set);
  }
  for (const line of [...notesByLine.keys()].sort((a, b) => lineRank(a) - lineRank(b))) {
    const set = notesByLine.get(line) ?? new Set<NoteId>();
    out.push({ kind: 'notes', line, notes: idx.notesByLine(line).filter((id) => set.has(id)) });
  }
  const lines = [...sel.lines].filter((l) => doc.lines.has(l)).sort((a, b) => lineRank(a) - lineRank(b));
  if (lines.length === 0 && activeLine !== null && doc.lines.has(activeLine)) lines.push(activeLine);
  for (const line of lines) out.push({ kind: 'line', line });
  const primary = lines[0];
  if (primary !== undefined) for (const which of CONTROL_WHICH) out.push({ kind: 'controls', line: primary, which });
  return out;
}

/** Same target identity, with the note list still valid in `doc`. */
export function targetStillValid(doc: ChartDoc, t: RawTarget): boolean {
  if (!doc.lines.has(t.line)) return false;
  switch (t.kind) {
    case 'events':
      return doc.tracks.has(t.track);
    case 'notes':
      return t.notes.length > 0 && t.notes.every((id) => doc.notes.get(id)?.line === t.line);
    case 'line':
    case 'controls':
      return true;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// The exported IR
// ---------------------------------------------------------------------------------------------------------------

/** Where the panel reads the exported IR of a line (the engine, or the lowering itself). */
export interface IrSource {
  irLine(line: LineId): IrLine;
}

/** The export settings of the document (what the engine derives from `doc.pkg`). */
export function lowerOptionsOf(doc: ChartDoc): LowerOptions {
  return { profile: doc.pkg.exportProfile, speedMode: doc.pkg.speedMode, speedBakeTolPx: doc.pkg.speedBakeTolPx };
}

/** Lowers lines on demand (cached), for when no engine is running. */
export function docIrSource(doc: ChartDoc, idx: DocIndexes): IrSource {
  const cache = new Map<LineId, IrLine>();
  const opts = lowerOptionsOf(doc);
  return {
    irLine(line) {
      let ir = cache.get(line);
      if (ir === undefined) {
        ir = Lower.lowerLine(doc, idx, line, opts);
        cache.set(line, ir);
      }
      return ir;
    },
  };
}

type EventList = readonly IrEvent<number | RGB | string>[];

/** The list of a property / layer in a lowered line, or undefined when the export writes none. */
export function listOf(ir: IrLine, prop: LineProp, layer: number): EventList | undefined {
  const key = listKeyOf(prop);
  if (PROP_SPECS[prop].layered) {
    const slot = ir.eventLayers[layer];
    if (slot === undefined || slot === null) return undefined;
    return (slot as unknown as Readonly<Record<string, EventList | undefined>>)[key];
  }
  if (ir.extended === null || ir.extended === 'absent') return undefined;
  const list = (ir.extended as unknown as Readonly<Record<string, EventList | undefined>>)[key];
  return list === undefined || list.length === 0 ? undefined : list;
}

const CONTROL_KEY: Readonly<Record<ControlWhich, 'posControl' | 'alphaControl' | 'sizeControl' | 'yControl'>> = {
  pos: 'posControl',
  alpha: 'alphaControl',
  size: 'sizeControl',
  y: 'yControl',
};

/** Fragment kind of a target. */
export function fragmentKindOf(t: RawTarget): RpeFragmentKind {
  return t.kind;
}

/** The exported fragment of a target, or null when the export writes nothing for it (e.g. a deleted track). */
export function currentFragment(t: RawTarget, src: IrSource): RpeFragmentIr | null {
  const ir = src.irLine(t.line);
  switch (t.kind) {
    case 'events': {
      const list = listOf(ir, t.prop, t.layer);
      const first = list?.[0];
      if (list === undefined || first === undefined) return null;
      const frag = { kind: 'events' as const, ir: [first, ...list.slice(1)] as const, list: listKeyOf(t.prop) };
      return frag;
    }
    case 'notes': {
      const byId = new Map<number, IrNote>();
      for (const n of ir.notes) if (n.srcNote !== null) byId.set(n.srcNote, n);
      const notes = t.notes.map((id) => byId.get(id)).filter((n): n is IrNote => n !== undefined);
      return notes.length === 0 ? null : { kind: 'notes', ir: notes };
    }
    case 'line':
      return { kind: 'line', ir };
    case 'controls': {
      const points = ir[CONTROL_KEY[t.which]] ?? [];
      const frag = { kind: 'controls' as const, ir: points, controlKey: t.which satisfies FragmentControlKey };
      return frag;
    }
  }
}

/** The text of a fragment as the exporter writes it. */
export function encodeFragment(frag: RpeFragmentIr, style: EncodeStyle): string {
  return encodeRpeFragment(frag, style);
}

// ---------------------------------------------------------------------------------------------------------------
// Checking edited text
// ---------------------------------------------------------------------------------------------------------------

export interface RawProblem {
  readonly level: 'error' | 'warning';
  /** JSON path inside the fragment ('' = the whole text). */
  readonly path: string;
  readonly message: MessageRef;
  /** Offset of the value in the text, when known. */
  readonly offset: number | null;
}

export interface RawCheck {
  readonly problems: readonly RawProblem[];
  /** The decoded fragment when it can be applied as written (no error). */
  readonly frag: RpeFragmentIr | null;
  /** The corrected text when normalisation would change the fragment (offered for review, never applied silently). */
  readonly corrected: string | null;
}

export interface RawContext {
  readonly doc: ChartDoc;
  readonly idx: DocIndexes;
  /** The exported fragment the text started from (diffing and path resolution). */
  readonly current: RpeFragmentIr | null;
  readonly style: EncodeStyle;
}

function textureKindIn(doc: ChartDoc): (path: string) => 'gif' | 'image' | null {
  const byPath = new Map([...doc.assets.values()].map((a) => [a.path, a]));
  return (path) => {
    const a = byPath.get(path);
    if (a === undefined) return path.toLowerCase().endsWith('.gif') ? 'gif' : null;
    return a.meta?.codec === 'gif' || a.mime === 'image/gif' ? 'gif' : 'image';
  };
}

function fragmentOptions(t: RawTarget, doc: ChartDoc, idx: DocIndexes): RpeFragmentOptions {
  const bpmList = idx.bpmSorted().flatMap((id) => {
    const p = doc.bpm.get(id);
    return p === undefined ? [] : [{ bpm: p.bpm, startTime: p.src?.rawStart ?? Beats.toTriple(p.t) }];
  });
  const base = { bpmList, speedMode: doc.pkg.speedMode, textureKind: textureKindIn(doc) };
  if (t.kind === 'events') return { ...base, list: listKeyOf(t.prop) as EventListKey };
  if (t.kind === 'controls') return { ...base, controlKey: t.which };
  return base;
}

/** Resolves package paths written in a fragment to assets: stored paths, and the paths the export used. */
export type AssetResolver = (path: string) => AssetId | null;

export function assetResolver(doc: ChartDoc, current: RpeFragmentIr | null): AssetResolver {
  const map = new Map<string, AssetId>();
  for (const a of doc.assets.values()) map.set(a.path, a.id);
  if (current?.kind === 'notes') {
    for (const n of current.ir) {
      const note = n.srcNote === null ? undefined : doc.notes.get(n.srcNote);
      if (n.hitsound !== undefined && note?.hitsound?.kind === 'asset') map.set(n.hitsound, note.hitsound.asset);
    }
  }
  if (current?.kind === 'line' && current.ir.srcLine !== null) {
    const tex = doc.lines.get(current.ir.srcLine)?.texture ?? null;
    if (tex !== null) map.set(current.ir.Texture, tex);
  }
  return (path) => map.get(path) ?? null;
}

const BUILTIN_HITSOUNDS: ReadonlyMap<string, 'tap' | 'drag' | 'flick'> = new Map([
  ['tap.mp3', 'tap'],
  ['drag.mp3', 'drag'],
  ['flick.mp3', 'flick'],
]);

function problem(level: RawProblem['level'], path: string, message: MessageRef, text: string): RawProblem {
  return { level, path, message, offset: offsetOfPath(text, path) };
}

/** Model-level checks of a decoded fragment (what the commands would refuse), as problems with paths. */
function validate(t: RawTarget, frag: RpeFragmentIr, ctx: RawContext, text: string): RawProblem[] {
  const out: RawProblem[] = [];
  const { doc } = ctx;
  const resolve = assetResolver(doc, ctx.current);
  switch (frag.kind) {
    case 'events': {
      if (t.kind !== 'events') break;
      const lifted = liftEventsChecked(frag.ir, t.prop, doc.pkg.speedMode);
      for (const i of lifted.skipped) {
        out.push(problem('error', `[${String(i)}]`, { key: m.eventSkipped, params: { at: `[${String(i)}]` } }, text));
      }
      break;
    }
    case 'notes': {
      if (t.kind !== 'notes') break;
      if (frag.ir.length !== t.notes.length) {
        out.push(problem('error', '', { key: m.notesCount, params: { count: t.notes.length } }, text));
        break;
      }
      const cur = ctx.current?.kind === 'notes' ? ctx.current.ir : [];
      frag.ir.forEach((n, i) => {
        const at = `[${String(i)}]`;
        if (n.hitsound !== undefined && !BUILTIN_HITSOUNDS.has(n.hitsound) && resolve(n.hitsound) === null) {
          out.push(problem('error', `${at}.hitsound`, { key: m.fileMissing, params: { path: n.hitsound } }, text));
        }
        const before = cur[i];
        const unchanged = before !== undefined && notesEqual(before, n);
        const s = tripleIn(n.startTime);
        const e = tripleIn(n.endTime);
        if (n.type === 2 && !unchanged && s !== null && e !== null && !Beats.lt(s.beat, e.beat)) {
          out.push(problem('error', `${at}.endTime`, { key: m.holdEnd }, text));
        }
      });
      break;
    }
    case 'line': {
      if (t.kind !== 'line') break;
      const ir = frag.ir;
      const count = doc.lineOrder.length;
      const self = ctx.idx.lineIndex(t.line);
      if (ir.father !== -1 && (ir.father < 0 || ir.father >= count || ir.father === self)) {
        out.push(problem('error', 'father', { key: m.fatherInvalid, params: { max: count - 1 } }, text));
      }
      if (ir.Texture !== 'line.png' && resolve(ir.Texture) === null) {
        out.push(problem('error', 'Texture', { key: m.fileMissing, params: { path: ir.Texture } }, text));
      }
      if (groupAt(doc, ir.Group) === undefined) {
        out.push(problem('error', 'Group', { key: m.groupInvalid, params: { group: ir.Group } }, text));
      }
      const cur = ctx.current?.kind === 'line' ? ctx.current.ir : null;
      if (cur !== null && (cur.isGif !== ir.isGif || cur.numOfNotes !== ir.numOfNotes)) {
        out.push(problem('warning', cur.isGif !== ir.isGif ? 'isGif' : 'numOfNotes', { key: m.rederived }, text));
      }
      break;
    }
    case 'controls':
      break;
  }
  return out;
}

/** Parses and checks edited text for a target. */
export function checkRawText(t: RawTarget, text: string, ctx: RawContext): RawCheck {
  let decoded: ReturnType<typeof decodeRpeFragmentDetailed>;
  try {
    decoded = decodeRpeFragmentDetailed(fragmentKindOf(t), text, fragmentOptions(t, ctx.doc, ctx.idx));
  } catch (err) {
    if (err instanceof RpeJsonSyntaxError) {
      return {
        problems: [{ level: 'error', path: '', message: err.reason, offset: err.error.offset }],
        frag: null,
        corrected: null,
      };
    }
    throw err;
  }
  const problems: RawProblem[] = [];
  for (const d of decoded.diagnostics) {
    const level = d.severity === 'note' ? 'warning' : 'error';
    problems.push(problem(level, d.path, diagnosticRef(d), text));
  }
  for (const r of decoded.report) {
    problems.push(problem('error', r.path ?? '', r.message, text));
  }
  let corrected: string | null = null;
  if (decoded.ir === null && decoded.normalized !== null) {
    try {
      corrected = encodeFragment(decoded.normalized, ctx.style);
    } catch {
      corrected = null;
    }
  }
  if (decoded.normalized === null && !problems.some((p) => p.level === 'error')) {
    problems.push(problem('error', '', { key: m.notRepresentable }, text));
  }
  if (decoded.ir !== null) problems.push(...validate(t, decoded.ir, ctx, text));
  const frag = problems.some((p) => p.level === 'error') ? null : decoded.ir;
  return { problems, frag, corrected };
}

// ---------------------------------------------------------------------------------------------------------------
// Comparing fragments (at the precision the file stores)
// ---------------------------------------------------------------------------------------------------------------

const fr = Math.fround;

function numEq(a: number, b: number): boolean {
  return a === b || fr(a) === fr(b) || (Number.isNaN(a) && Number.isNaN(b));
}

function valueEq(a: number | RGB | string, b: number | RGB | string): boolean {
  if (typeof a === 'number' && typeof b === 'number') return numEq(a, b);
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  if (typeof a === 'number' || typeof b === 'number') return false;
  return a.every((v, i) => v === b[i]);
}

function jsonEq(a: Json | undefined, b: Json | undefined): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function eventEq(a: IrEvent<number | RGB | string>, b: IrEvent<number | RGB | string>): boolean {
  return (
    tripleEquals(a.startTime, b.startTime) &&
    tripleEquals(a.endTime, b.endTime) &&
    valueEq(a.start, b.start) &&
    valueEq(a.end, b.end) &&
    a.easingType === b.easingType &&
    numEq(a.easingLeft, b.easingLeft) &&
    numEq(a.easingRight, b.easingRight) &&
    a.bezier === b.bezier &&
    a.bezierPoints.every((v, i) => numEq(v, b.bezierPoints[i] ?? NaN)) &&
    a.linkgroup === b.linkgroup &&
    jsonEq(a.extra, b.extra)
  );
}

export function eventsEqual(a: EventList, b: EventList): boolean {
  return a.length === b.length && a.every((e, i) => eventEq(e, b[i] as IrEvent<number | RGB | string>));
}

function optEq<T>(a: T | undefined, b: T | undefined, eq: (x: T, y: T) => boolean): boolean {
  return a === undefined || b === undefined ? a === b : eq(a, b);
}

export function notesEqual(a: IrNote, b: IrNote): boolean {
  return (
    a.type === b.type &&
    tripleEquals(a.startTime, b.startTime) &&
    tripleEquals(a.endTime, b.endTime) &&
    numEq(a.positionX, b.positionX) &&
    a.above === b.above &&
    a.isFake === b.isFake &&
    numEq(a.speed, b.speed) &&
    numEq(a.yOffset, b.yOffset) &&
    numEq(a.size, b.size) &&
    a.alpha === b.alpha &&
    numEq(a.visibleTime, b.visibleTime) &&
    a.hitsound === b.hitsound &&
    optEq(a.tint, b.tint, (x, y) => valueEq(x, y)) &&
    optEq(a.tintHitEffects, b.tintHitEffects, (x, y) => valueEq(x, y)) &&
    optEq(a.judgeArea, b.judgeArea, numEq) &&
    jsonEq(a.extra, b.extra)
  );
}

function pointEq(a: IrControlPoint, b: IrControlPoint): boolean {
  return numEq(a.x, b.x) && a.easing === b.easing && numEq(a.value, b.value) && jsonEq(a.extra, b.extra);
}

export function controlsEqual(
  a: readonly IrControlPoint[] | undefined,
  b: readonly IrControlPoint[] | undefined,
): boolean {
  const x = a ?? [];
  const y = b ?? [];
  return x.length === y.length && x.every((p, i) => pointEq(p, y[i] as IrControlPoint));
}

// ---------------------------------------------------------------------------------------------------------------
// Applying
// ---------------------------------------------------------------------------------------------------------------

/** The group at a judgeLineGroup index: an id, null for "Default", undefined when the index names no group. */
export function groupAt(doc: Pick<ChartDoc, 'groups'>, index: number): GroupId | null | undefined {
  const layout = groupLayout(doc);
  for (const [id, i] of layout.index) if (i === index) return id;
  return index === 0 ? null : undefined;
}

const NOTE_KIND: Readonly<Record<IrNote['type'], NoteKind>> = { 1: 'tap', 2: 'hold', 3: 'flick', 4: 'drag' };

type NoteFields = Omit<Note, 'id' | 'line' | 'gen'>;

/** The note record an IR note describes (the inverse of the exporter's note lowering). */
export function noteFromIr(n: IrNote, resolve: AssetResolver): NoteFields | null {
  const s = tripleIn(n.startTime);
  const e = tripleIn(n.endTime);
  if (s === null || e === null) return null;
  const kind = NOTE_KIND[n.type];
  const src: { -readonly [K in keyof NoteSrc]: NoteSrc[K] } = {};
  if (s.raw !== undefined) src.rawStart = s.raw;
  let end = s.beat;
  if (kind === 'hold') {
    end = Beats.max(s.beat, e.beat);
    if (e.raw !== undefined && Beats.eq(end, e.beat)) src.rawEnd = e.raw;
  } else if (!tripleEquals(n.endTime, n.startTime)) src.nonHoldEnd = n.endTime;
  if (n.above !== 1 && n.above !== 2) src.aboveRaw = n.above;
  if (n.isFake !== 0 && n.isFake !== 1) src.isFakeRaw = n.isFake;
  if (n.extra.length > 0) src.extra = n.extra;
  let hitsound: Hitsound | null = null;
  if (n.hitsound !== undefined) {
    const builtin = BUILTIN_HITSOUNDS.get(n.hitsound);
    if (builtin !== undefined) hitsound = { kind: 'builtin', sound: builtin };
    else {
      const asset = resolve(n.hitsound);
      if (asset === null) return null;
      hitsound = { kind: 'asset', asset };
    }
  }
  return {
    kind,
    t: s.beat,
    end,
    x: n.positionX,
    above: n.above === 1,
    fake: n.isFake !== 0,
    speed: n.speed,
    yOffset: n.yOffset,
    size: n.size,
    alpha: n.alpha,
    visibleTime: n.visibleTime === RPE_VISIBLE_ALWAYS ? null : n.visibleTime,
    hitsound,
    tint: n.tint === undefined ? null : [n.tint[0], n.tint[1], n.tint[2]],
    tintHitEffects:
      n.tintHitEffects === undefined ? null : [n.tintHitEffects[0], n.tintHitEffects[1], n.tintHitEffects[2]],
    judgeArea: n.judgeArea ?? null,
    ...(Object.keys(src).length > 0 ? { src } : {}),
  };
}

function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

const NOTE_FIELDS = [
  'kind',
  't',
  'end',
  'x',
  'above',
  'fake',
  'speed',
  'yOffset',
  'size',
  'alpha',
  'visibleTime',
  'hitsound',
  'tint',
  'tintHitEffects',
  'judgeArea',
  'src',
] as const;

export interface RawApplyArgs {
  readonly target: RawTarget;
  /** The decoded fragment to apply (`RawCheck.frag`). */
  readonly next: RpeFragmentIr;
  /** The exported fragment the edit started from; null applies everything (no diff). */
  readonly current: RpeFragmentIr | null;
}

export interface RawApplyResult {
  /** Items changed (events lists, notes, line fields, control curves). */
  readonly changed: number;
}

function reason(key: string, params?: Readonly<Record<string, string | number>>): MessageRef {
  return params === undefined ? { key } : { key, params };
}

function applyEvents(
  tx: Parameters<Cmd<RawApplyArgs>>[0],
  t: Extract<RawTarget, { kind: 'events' }>,
  a: RawApplyArgs,
): number {
  if (a.next.kind !== 'events') return tx.fail(reason(m.kindMismatch));
  if (a.current?.kind === 'events' && eventsEqual(a.current.ir, a.next.ir)) return 0;
  if (!tx.doc.tracks.has(t.track)) tx.fail(reason(m.targetGone));
  const lifted = liftEventsChecked(a.next.ir, t.prop, tx.doc.pkg.speedMode);
  const skipped = lifted.skipped[0];
  if (skipped !== undefined) tx.fail(reason(m.eventSkipped, { at: `[${String(skipped)}]` }));
  Core.keys.replaceTrackKeys(tx, { track: t.track, keys: lifted.keys });
  return 1;
}

function applyNotes(
  tx: Parameters<Cmd<RawApplyArgs>>[0],
  t: Extract<RawTarget, { kind: 'notes' }>,
  a: RawApplyArgs,
): number {
  if (a.next.kind !== 'notes') return tx.fail(reason(m.kindMismatch));
  if (a.next.ir.length !== t.notes.length) tx.fail(reason(m.notesCount, { count: t.notes.length }));
  const cur = a.current?.kind === 'notes' ? a.current.ir : null;
  const resolve = assetResolver(tx.doc, a.current);
  let changed = 0;
  a.next.ir.forEach((n, i) => {
    const before = cur?.[i];
    if (before !== undefined && notesEqual(before, n)) return;
    const id = t.notes[i] as NoteId;
    const rec = tx.doc.notes.get(id) ?? tx.fail(reason(m.targetGone));
    const fields = noteFromIr(n, resolve);
    if (fields === null) tx.fail(reason(m.fileMissing, { path: n.hitsound ?? '' }));
    if (
      fields.kind === 'hold' &&
      !Beats.lt(fields.t, fields.end) &&
      !(rec.kind === 'hold' && Beats.eq(rec.t, rec.end))
    ) {
      tx.fail(reason(m.holdEnd));
    }
    const patch: Record<string, unknown> = {};
    for (const f of NOTE_FIELDS) {
      const next = fields[f];
      if (!sameJson(rec[f], next)) patch[f] = next;
    }
    if (Object.keys(patch).length === 0) return;
    tx.update('notes', id, patch);
    changed++;
  });
  return changed;
}

type LinePatch = { -readonly [K in 'name' | 'zOrder' | 'cover' | 'group' | 'rotateWithParent']?: Line[K] };

function applyLine(
  tx: Parameters<Cmd<RawApplyArgs>>[0],
  t: Extract<RawTarget, { kind: 'line' }>,
  a: RawApplyArgs,
): number {
  if (a.next.kind !== 'line') return tx.fail(reason(m.kindMismatch));
  const nxt = a.next.ir;
  const cur = a.current?.kind === 'line' ? a.current.ir : null;
  const line = tx.doc.lines.get(t.line) ?? tx.fail(reason(m.targetGone));
  const resolve = assetResolver(tx.doc, a.current);
  const differs = <K extends keyof IrLine>(k: K, eq: (x: IrLine[K], y: IrLine[K]) => boolean = Object.is): boolean =>
    cur === null || !eq(cur[k], nxt[k]);
  let changed = 0;
  const patch: LinePatch = {};
  if (differs('Name')) patch.name = nxt.Name;
  if (differs('rotateWithFather')) patch.rotateWithParent = nxt.rotateWithFather;
  if (differs('zOrder')) patch.zOrder = nxt.zOrder;
  if (differs('isCover')) patch.cover = nxt.isCover === 1;
  if (differs('Group')) {
    const g = groupAt(tx.doc, nxt.Group);
    if (g === undefined) tx.fail(reason(m.groupInvalid, { group: nxt.Group }));
    patch.group = g;
  }
  const changedFields = Object.keys(patch).filter(
    (k) => !sameJson(line[k as keyof LinePatch], patch[k as keyof LinePatch]),
  );
  if (changedFields.length > 0) {
    Core.lines.patchLine(tx, { id: t.line, patch });
    changed += changedFields.length;
  }
  if (differs('Texture')) {
    const asset = nxt.Texture === 'line.png' ? null : resolve(nxt.Texture);
    if (asset === null && nxt.Texture !== 'line.png') tx.fail(reason(m.fileMissing, { path: nxt.Texture }));
    if (asset !== line.texture) {
      Core.lines.setTexture(tx, { line: t.line, asset });
      changed++;
    }
  }
  if (differs('father')) {
    const parent = nxt.father === -1 ? null : (tx.doc.lineOrder[nxt.father] ?? null);
    if (nxt.father !== -1 && (parent === null || parent === t.line)) {
      tx.fail(reason(m.fatherInvalid, { max: tx.doc.lineOrder.length - 1 }));
    }
    if (parent !== line.parent) {
      Core.lines.setParent(tx, { line: t.line, parent, mode: 'keepNumbers', atSec: 0 });
      changed++;
    }
  }
  if (differs('attachUI') && nxt.attachUI !== line.attachUI) {
    Core.lines.setAttachUI(tx, { line: t.line, element: nxt.attachUI });
    changed++;
  }
  for (const which of CONTROL_WHICH) {
    const key = CONTROL_KEY[which];
    if (cur !== null && controlsEqual(cur[key], nxt[key]) && (cur[key] === undefined) === (nxt[key] === undefined))
      continue;
    const points = nxt[key];
    Core.lines.setControl(tx, { line: t.line, which, curve: points === undefined ? null : controlIn(points) });
    changed++;
  }
  // RPE-only fields Phira ignores (kept verbatim for RPE).
  const rec = tx.doc.lines.get(t.line) ?? line;
  const rpe = { ...rec.rpe };
  let rpeChanged = false;
  if (differs('bpmfactor', numEq)) {
    rpe.bpmfactor = nxt.bpmfactor;
    rpeChanged = true;
  }
  if (differs('anchor', (x, y) => numEq(x[0], y[0]) && numEq(x[1], y[1]))) {
    rpe.anchor = [nxt.anchor[0], nxt.anchor[1]];
    rpeChanged = true;
  }
  if (differs('isCover')) {
    const raw = nxt.isCover !== 0 && nxt.isCover !== 1 ? nxt.isCover : null;
    if (raw !== rpe.isCoverRaw) {
      rpe.isCoverRaw = raw;
      rpeChanged = true;
    }
  }
  if (differs('skewControl', sameJson)) {
    rpe.skewControl = nxt.skewControl ?? null;
    rpeChanged = true;
  }
  if (differs('extra', sameJson)) {
    rpe.extra = nxt.extra;
    rpeChanged = true;
  }
  if (rpeChanged) {
    tx.update('lines', t.line, { rpe });
    changed++;
  }
  if (cur !== null && (cur.isGif !== nxt.isGif || cur.numOfNotes !== nxt.numOfNotes)) tx.note(reason(m.rederived));
  return changed;
}

function applyControls(
  tx: Parameters<Cmd<RawApplyArgs>>[0],
  t: Extract<RawTarget, { kind: 'controls' }>,
  a: RawApplyArgs,
): number {
  if (a.next.kind !== 'controls') return tx.fail(reason(m.kindMismatch));
  if (a.current?.kind === 'controls' && controlsEqual(a.current.ir, a.next.ir)) return 0;
  if (!tx.doc.lines.has(t.line)) tx.fail(reason(m.targetGone));
  Core.lines.setControl(tx, {
    line: t.line,
    which: t.which,
    curve: a.next.ir.length === 0 ? null : controlIn(a.next.ir),
  });
  return 1;
}

/** Applies an edited fragment (one transaction; see the module header). */
export const applyRawEdit: Cmd<RawApplyArgs, RawApplyResult> = (tx, a) => {
  const t = a.target;
  let changed: number;
  switch (t.kind) {
    case 'events':
      changed = applyEvents(tx, t, a);
      break;
    case 'notes':
      changed = applyNotes(tx, t, a);
      break;
    case 'line':
      changed = applyLine(tx, t, a);
      break;
    case 'controls':
      changed = applyControls(tx, t, a);
      break;
  }
  tx.setFocus({ line: t.line });
  return { changed };
};
